import { createAction, createSlice } from '@reduxjs/toolkit';
import { extractSharedActions } from '@sda/utils/store';
import { updateCurrentSessionUser } from '@client/slices/helpers/session.reducer';
import { Reducers } from '@sda/enums/store';
import { createEntityAdapter } from '@client/store/entity.adapter';
import { ProfileTypes } from '@sda/enums';
import { getDateTime } from '@sda/utils/date';
import type { ExtendedUser } from '@sda/interfaces/reducer/session.reducer';
import type { GenericList } from '@sda/helpers/helpers.types';
import type { PayloadAction } from '@reduxjs/toolkit';
import type {
  VanityUrlPayload, UpdateVenuePayload, VenuesState, SignupVenuePayload,
} from '@sda/interfaces/reducer/venues.reducer';
import type { PublicVenue, Venue } from '@sda/interfaces/venue';
import type { FetchDataType } from '@sda/sdk/interfaces';

// LISTENERS

export const startCreatePartialVenue = createAction<Pick<SignupVenuePayload, 'compiledAddress' | 'latLng' | 'name' | 'googlePlaceId'>>(`${Reducers.VENUES}/startCreatePartialVenue`);
export const startCreateVenue = createAction<SignupVenuePayload>(`${Reducers.VENUES}/startCreateVenue`);
export const startPatchVenue = createAction<SignupVenuePayload>(`${Reducers.VENUES}/startPatchVenue`);
export const startUpdateVenue = createAction<UpdateVenuePayload>(`${Reducers.VENUES}/startUpdateVenue`);
export const startUpdateVenueVanityUrl = createAction<VanityUrlPayload>(`${Reducers.VENUES}/startUpdateVenueVanityUrl`);
export const startToggleVenueState = createAction<string>(`${Reducers.VENUES}/startToggleVenueState`);

/**
 * data fetching
 */
export const startFetchFullVenue = createAction<string>(`${Reducers.VENUES}/startFetchFullVenue`);
export const startFetchPublicVenueDetails = createAction<string>(`${Reducers.VENUES}/startFetchPublicVenueDetails`);
export const unsubscribeVenue = createAction<string>(`${Reducers.VENUES}/unsubscribeVenue`);

export const venueAdapter = createEntityAdapter<Venue & FetchDataType>({
  sortComparer: (a, b) => a.name?.localeCompare(b.name) || getDateTime(b.createdAt).toMillis() - getDateTime(a.createdAt).toMillis(),
});

const initialState: VenuesState = {
  selectedProfileId: null,
};

const venuesSlice = createSlice({
  initialState: venueAdapter.getInitialState<VenuesState>(initialState),
  name: Reducers.VENUES,
  reducers: {
    ...extractSharedActions(venueAdapter),
    upsertPublicVenue: (state, action: PayloadAction<PublicVenue & FetchDataType>) => {
      state.status = true;

      venueAdapter.upsertOne(state, action.payload as Venue & FetchDataType);
    },
    upsertPublicVenues: (state, action: PayloadAction<GenericList<PublicVenue & FetchDataType>>) => {
      state.status = true;

      venueAdapter.upsertMany(state, action.payload as GenericList<Venue & FetchDataType>);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCurrentSessionUser, (state, action: PayloadAction<ExtendedUser>) => {
        if (action.payload?.selectedProfileId) {
          state.selectedProfileId = action.payload?.selectedProfileType === ProfileTypes.VENUE ? action.payload.selectedProfileId : null;
        }
      });
  },
});

export const {
  upsertOne: upsertVenue, updateOne: updateVenue, upsertPublicVenue, upsertPublicVenues,
} = venuesSlice.actions;

export default venuesSlice.reducer;
